import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import ThemeToggle from "@/components/sybrax/ThemeToggle";
import LanguageToggle from "@/components/sybrax/LanguageToggle";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const LogoMark = ({ className = "" }) => (
    <>
        <img
            src="/logo-light.png"
            alt="Sybrax"
            data-testid="logo-light-img"
            className={`logo-light ${className}`}
            draggable="false"
        />
        <img
            src="/logo-dark.png"
            alt="Sybrax"
            data-testid="logo-dark-img"
            className={`logo-dark ${className}`}
            draggable="false"
        />
    </>
);

const slugFor = (href) => href.replace("#", "");

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { lang } = useLanguage();
    const nav = content[lang].nav;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleClick = (e, href) => {
        e.preventDefault();
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setMobileOpen(false);
    };

    return (
        <header
            data-testid="sybrax-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/80 dark:bg-[#030712]/80 backdrop-blur-xl border-b border-slate-200/70 dark:border-white/5"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3 sm:gap-4">
                <a
                    href="#hero"
                    data-testid="nav-logo"
                    onClick={(e) => handleClick(e, "#hero")}
                    aria-label="Sybrax — go to top"
                    className="flex items-center shrink-0"
                >
                    <LogoMark className="h-7 sm:h-8 w-auto select-none" />
                </a>

                <ul className="hidden lg:flex items-center gap-1">
                    {nav.items.map((item) => (
                        <li key={item.href}>
                            <a
                                href={item.href}
                                data-testid={`nav-${slugFor(item.href)}-link`}
                                onClick={(e) => handleClick(e, item.href)}
                                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white transition-colors rounded-full hover:bg-slate-100/80 dark:hover:bg-white/5"
                            >
                                {item.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-2 sm:gap-3">
                    <LanguageToggle />
                    <ThemeToggle />
                    <a
                        href="#contact"
                        data-testid="nav-cta-contact"
                        onClick={(e) => handleClick(e, "#contact")}
                        className="hidden lg:inline-flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#030712] text-sm font-semibold px-5 py-2.5 transition-all hover:shadow-[0_0_24px_rgba(6,182,212,0.5)]"
                    >
                        {nav.cta}
                    </a>
                    <button
                        type="button"
                        data-testid="nav-mobile-toggle"
                        onClick={() => setMobileOpen((s) => !s)}
                        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-300/70 dark:border-white/10 text-slate-700 dark:text-slate-200 bg-white/70 dark:bg-white/[0.04] hover:bg-white dark:hover:bg-white/[0.08]"
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </nav>

            {/* Mobile menu */}
            <div
                data-testid="nav-mobile-menu"
                className={`lg:hidden overflow-hidden transition-all duration-300 ${
                    mobileOpen
                        ? "max-h-[520px] border-b border-slate-200/70 dark:border-white/5"
                        : "max-h-0"
                } bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl`}
            >
                <ul className="px-5 sm:px-6 py-4 flex flex-col gap-1">
                    {nav.items.map((item) => (
                        <li key={item.href}>
                            <a
                                href={item.href}
                                data-testid={`nav-mobile-${slugFor(item.href)}-link`}
                                onClick={(e) => handleClick(e, item.href)}
                                className="block px-4 py-3 text-base font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-white/5 rounded-lg"
                            >
                                {item.label}
                            </a>
                        </li>
                    ))}
                    <li className="pt-2">
                        <a
                            href="#contact"
                            data-testid="nav-mobile-cta-contact"
                            onClick={(e) => handleClick(e, "#contact")}
                            className="block text-center rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#030712] text-sm font-semibold px-5 py-3 transition-all"
                        >
                            {nav.cta}
                        </a>
                    </li>
                </ul>
            </div>
        </header>
    );
};

export default Navbar;
